import type {
  MessageAppend,
  TransactionButtonProps,
  TransactionContainerProps,
} from '@bitte-ai/types';
import {
  MessageCompiledInstruction,
  VersionedTransaction,
} from '@solana/web3.js';
import { useEffect, useReducer, useRef } from 'react';
import { useTransaction } from '../../../hooks/useTransaction';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { createDeclineTransactionMessage } from '../../../lib/transaction-messages';
import { useAccount } from '../../../state/wallet.state';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { Button } from '../../ui/button';
import { CardFooter, CardHeader } from '../../ui/card';
import DefaultTxApproveButton from '../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../loading/LoadingMessage';
import { CopyStandard } from './../CopyStandard';
import { TransactionResult } from './TransactionResult';

// Solana transaction data interfaces
interface SolanaTransactionData {
  instructions: MessageCompiledInstruction[];
  recentBlockhash: string | null;
  sender: string | null;
}

interface SolanaTransactionError {
  error: string;
}

type SolanaTransactionDataState =
  | SolanaTransactionData
  | SolanaTransactionError
  | null;

// Type guard functions
function isSolanaTransactionData(
  data: SolanaTransactionDataState,
): data is SolanaTransactionData {
  return data !== null && 'instructions' in data;
}

function isSolanaTransactionError(
  data: SolanaTransactionDataState,
): data is SolanaTransactionError {
  return data !== null && 'error' in data;
}

// State interface for the reducer
interface SolanaTxState {
  errorMsg: string;
  isLoading: boolean;
  isDeclined: boolean;
  txSignature: string | undefined;
  transaction: VersionedTransaction | null;
  transactionData: SolanaTransactionDataState;
}

// Action types
type SolanaTxAction =
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DECLINED'; payload: boolean }
  | { type: 'SET_TX_SIGNATURE'; payload: string }
  | { type: 'SET_TRANSACTION'; payload: VersionedTransaction }
  | { type: 'SET_TRANSACTION_DATA'; payload: SolanaTransactionDataState }
  | { type: 'RESET_TRANSACTION_STATE' };

// Initial state
const initialState: SolanaTxState = {
  errorMsg: '',
  isLoading: false,
  isDeclined: false,
  txSignature: undefined,
  transaction: null,
  transactionData: null,
};

// Reducer function
function solanaTxReducer(
  state: SolanaTxState,
  action: SolanaTxAction,
): SolanaTxState {
  switch (action.type) {
    case 'SET_ERROR':
      return { ...state, errorMsg: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, errorMsg: '' };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_DECLINED':
      return { ...state, isDeclined: action.payload, isLoading: false };
    case 'SET_TX_SIGNATURE':
      return { ...state, txSignature: action.payload, isLoading: false };
    case 'SET_TRANSACTION':
      return { ...state, transaction: action.payload };
    case 'SET_TRANSACTION_DATA':
      return { ...state, transactionData: action.payload };
    case 'RESET_TRANSACTION_STATE':
      return { ...state, isLoading: false, errorMsg: '', isDeclined: false };
    default:
      return state;
  }
}

export const SolanaTxCard = ({
  transactionBytes,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
}: {
  transactionBytes?: string;
  messageBackgroundColor: string;
  borderColor: string;
  textColor: string;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  append: MessageAppend;
  messageId: string;
}) => {
  const { width } = useWindowSize();
  const isMobile = !!width && width < 640;
  const [state, dispatch] = useReducer(solanaTxReducer, initialState);
  const {
    errorMsg,
    isLoading,
    isDeclined,
    txSignature,
    transaction,
    transactionData,
  } = state;
  const { solanaWallet, solanaConnection } = useAccount();
  const messageAppended = useRef(false);

  const { handleTxn } = useTransaction({ solanaWallet, solanaConnection });

  useEffect(() => {
    if (transactionBytes && !transaction) {
      try {
        const bytes = Buffer.from(transactionBytes, 'base64');
        const versionedTx = VersionedTransaction.deserialize(bytes);
        dispatch({ type: 'SET_TRANSACTION', payload: versionedTx });

        try {
          const txData = {
            instructions: versionedTx.message.compiledInstructions || [],
            recentBlockhash: versionedTx.message.recentBlockhash || null,
            sender: solanaWallet?.publicKey?.toString() || null,
          };
          dispatch({ type: 'SET_TRANSACTION_DATA', payload: txData });
        } catch (error) {
          console.error('Error extracting Solana transaction data:', error);
          dispatch({
            type: 'SET_TRANSACTION_DATA',
            payload: { error: 'Failed to extract transaction data' },
          });
        }
      } catch (error) {
        console.error('Failed to parse Solana transaction bytes:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to parse transaction data',
        });
      }
    }
  }, [transactionBytes, transaction]);

  if (!transactionBytes && !transaction && !transactionData) {
    return (
      <p className="bitte-my-4 bitte-overflow-auto bitte-text-center">
        Unable to create Solana transaction.
      </p>
    );
  }

  const handleSmartAction = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await handleTxn({
        solanaTransaction: transaction || undefined,
      });

      if (result?.solana?.signature) {
        dispatch({
          type: 'SET_TX_SIGNATURE',
          payload: result.solana.signature,
        });
        if (!messageAppended.current) {
          messageAppended.current = true;
          await append({
            id: messageId,
            role: 'assistant',
            content: `Transaction successfully submitted. Transaction signature: ${result.solana.signature}\nView on Solana Explorer: https://explorer.solana.com/tx/${result.solana.signature}\n`,
            createdAt: new Date(),
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Unknown error: ${String(error)}`;

      dispatch({ type: 'SET_ERROR', payload: errorMessage });

      if (!messageAppended.current) {
        messageAppended.current = true;
        await append({
          id: messageId,
          role: 'assistant',
          content: `Transaction failed: ${errorMessage}`,
          createdAt: new Date(),
        });
      }
    }
  };

  const declineTransaction = async () => {
    dispatch({ type: 'SET_DECLINED', payload: true });
    await append(createDeclineTransactionMessage(messageId));
  };

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-border-b bitte-p-4 bitte-text-center bitte-md:p-6"
        style={{ borderColor: borderColor }}
      >
        <p className="bitte-text-xl bitte-font-semibold">Solana Transaction</p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <Accordion type="single" collapsible defaultValue="txData">
            <AccordionItem value="txData">
              <AccordionTrigger className="bitte-pt-0">
                <div className="bitte-flex bitte-items-center bitte-justify-between bitte-text-sm">
                  <p>Transaction Details</p>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <pre className="bitte-whitespace-pre-wrap bitte-overflow-auto bitte-max-h-60 bitte-text-xs">
                  {isSolanaTransactionData(transactionData)
                    ? JSON.stringify(transactionData.instructions, null, 2)
                    : isSolanaTransactionError(transactionData)
                      ? `Error: ${transactionData.error}`
                      : 'No transaction data available'}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {solanaWallet?.publicKey && (
            <div className="bitte-flex bitte-items-center bitte-justify-between">
              <span>Sender:</span>
              <CopyStandard
                text={solanaWallet.publicKey.toBase58()}
                textSize="sm"
                charSize={isMobile ? 7 : 12}
              />
            </div>
          )}
        </div>
      </div>

      {errorMsg ? (
        <div className="bitte-flex bitte-flex-col bitte-items-center bitte-gap-4 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          <p className="bitte-text-red-300">
            An error occurred trying to execute your transaction: {errorMsg}.
          </p>
          <Button
            className="bitte-w-1/2"
            variant="outline"
            onClick={() => {
              dispatch({ type: 'CLEAR_ERROR' });
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : isLoading ? (
        <LoadingMessage color={textColor} />
      ) : null}

      {isDeclined ? (
        <div className="bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          Transaction declined!
        </div>
      ) : null}

      {txSignature ? (
        <div className="bitte-p-6">
          <TransactionResult
            result={{ solana: { signature: txSignature } }}
            accountId={solanaWallet?.publicKey?.toString() || ''}
            textColor={textColor}
          />
          <div className="bitte-mt-2 bitte-text-center">
            <a
              href={`https://explorer.solana.com/tx/${txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bitte-text-blue-500 hover:bitte-text-blue-700 bitte-underline"
            >
              View on Solana Explorer
            </a>
          </div>
        </div>
      ) : null}

      {!isLoading && !errorMsg && !txSignature && !isDeclined && (
        <CardFooter className="bitte-flex bitte-items-center bitte-gap-6">
          <>
            <DeclineButton onClick={declineTransaction} disabled={isLoading} />
            <ApproveButton
              onClick={handleSmartAction}
              disabled={isLoading || !solanaWallet?.connected}
              isLoading={isLoading}
            />
          </>
        </CardFooter>
      )}
    </TxContainer>
  );
};
